import { useImperativeHandle, useMemo, useRef, useState } from "react";

import { FieldChangeHandlerContext } from "@mui/x-date-pickers/internals";
import { DateRange, TimeRangeValidationError } from "@mui/x-date-pickers-pro";
import { Dayjs } from "dayjs";

import { UseTimeRangeFieldParams } from "./TimeRangeField.types";

export const useTimeRangeField = <R>({
  ref,
  defaultValue,
  value,
  onChange,
}: UseTimeRangeFieldParams<R>) => {
  const timeRangeFieldRef = useRef<HTMLInputElement | null>(null);
  const [state, setState] = useState<DateRange<Dayjs>>(
    value ?? defaultValue ?? [null, null]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: timeRangeFieldRef.current,
        value: state,
        setValue: (v: DateRange<Dayjs>) => {
          setState(v);
        },
        reset: () => {
          setState([null, null]);
        },
      };
    }) as unknown as () => R,
    [state]
  );

  const onChangeHandler = (
    newValue: DateRange<Dayjs>,
    context: FieldChangeHandlerContext<TimeRangeValidationError>
  ) => {
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue, context);
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state),
    [value, state]
  );

  return {
    timeRangeFieldRef,
    valueState,
    onChangeHandler,
  };
};
